import { ErrorOutline, MyLocation, OpenInNew, WarningAmber } from "@mui/icons-material";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import type { i18n as I18n, TFunction } from "i18next";
import { FlussValidationError } from "modules/fluss/types/fluss";
import { flussNodeTypeLabel } from "./node-properties/fluss-node-type-label";
import { useTranslation } from "react-i18next";

export const VALIDATION_MESSAGE_DEFAULTS: Record<string, string> = {
    invalid_xml: "The process XML is malformed.",
    no_process: "The BPMN file does not contain a process.",
    multiple_participants_not_supported: "Multiple BPMN pools or participants are not supported yet.",
    duplicate_element_id: "Two elements use the same identifier.",
    unsupported_type: "This BPMN element type is not supported yet.",
    link_throw_without_catch: "No Link Catch with this name exists in the same scope as this Link Throw.",
    duplicate_link_catch: "More than one Link Catch uses this name in the same scope; rename one of them.",
    link_catch_without_throw: "No Link Throw points to this Link Catch; it will never be reached.",
    invalid_edge_priority: "Set a valid non-negative priority for this path.",
    duplicate_sequence_flow: "Remove the duplicate path between these nodes.",
    multiple_outgoing_flows: "Use a gateway to route multiple outgoing paths.",
    unknown_condition_operator: "Choose an operator supported by Fluss.",
    missing_start: "Add a start event to the process.",
    start_without_trigger: "The start event has no trigger; this process can only be started by another process or the API.",
    missing_end: "Add at least one end event to the process.",
    dangling_flow: "This path points to an element that no longer exists.",
    disconnected_node: "Connect this node to a path reachable from the start.",
    gateway_missing_default: "Choose a default path for this decision.",
    gateway_unconditional_nondefault_flow: "Add a condition to each non-default gateway path.",
    inclusive_gateway_unverified_exhaustive: "No default path is set; the coverage of all cases was confirmed manually and is not automatically verified.",
    invalid_event_gateway_outgoing: "Connect this gateway to at least two signal or timer catch events, without conditions or a default path.",
    unresolved_binding: "A table, field, rule or template used by this node is unavailable.",
    unresolved_email_template: "Choose an email template that exists and is active.",
    unresolved_event_type: "Choose an event type that exists in the catalog.",
    inactive_event_type: "The selected event type is inactive. Publishing can continue after confirmation.",
    invalid_signal_binding: "Review the event correlation and payload mappings.",
    invalid_timer_binding: "Complete the timer configuration with valid values.",
    invalid_timer_cron: "Enter a valid cron expression for the timer.",
    invalid_timer_field: "Choose an existing DATE or DATETIME field for the timer.",
    invalid_trigger_binding: "Complete the start trigger configuration.",
    invalid_trigger_condition: "Review the start trigger condition.",
    invalid_trigger_mapping: "Review the initial variable mappings.",
    invalid_node_action_binding: "This action is not compatible with the selected node.",
    invalid_terminate_binding: "Complete the terminate action configuration.",
    invalid_milestone_binding: "Give the milestone a name and complete its configuration.",
    invalid_end_binding: "Review the end action mappings.",
    invalid_field_mapping: "Review the source and destination field mapping.",
    invalid_factory_action: "Review the selected factory action.",
    unresolved_factory_action: "Choose an action that exists in the catalog.",
    inactive_factory_action: "The selected factory action is inactive.",
    invalid_factory_action_schema: "The selected action has an invalid parameter definition.",
    missing_factory_action_param: "Complete every required action parameter.",
    unresolved_factory_action_param: "A catalog reference used by this action is unavailable.",
    invalid_factory_action_param: "Review the action parameter values.",
    plaintext_secret_forbidden: "Use a vault reference instead of a plain-text credential.",
    unknown_property: "Create or replace the referenced system property.",
    unknown_node_output: "Choose an output that this node produces.",
    unreachable_node_output: "Use an output from a node that can run before this point.",
    invalid_data_reference: "Choose a valid data reference.",
    invalid_data_reference_name: "Choose a process variable whose name differs from a node ID.",
    invalid_data_reference_column: "Choose a column that exists in the referenced table.",
    invalid_data_reference_type: "Choose a source with a compatible type.",
    invalid_data_reference_table: "Choose a record from the required table.",
    invalid_boundary_binding: "Attach this boundary event to an existing node in the same process, with at least one outgoing path.",
    invalid_boundary_error_binding: "Complete the captured error configuration and the variable name to store its details.",
    invalid_boundary_conditional_binding: "Define the condition this boundary event should watch for.",
    invalid_user_task_wait_binding: "Review the User Task wait for completion configuration.",
    user_task_does_not_wait: "This task does not wait for completion: the flow continues as soon as it is created.",
    invalid_subprocess_scope: "A subprocess needs exactly one internal start and at least one internal end.",
    invalid_event_subprocess_scope: "An event sub-process needs exactly one typed internal start with no incoming path, and at least one internal end.",
    interruptive_event_subprocess_timer: "This event cancels the process when it fires.",
    invalid_call_activity_binding: "Complete the called process configuration (process, version, failure policy).",
    unresolved_call_activity_definition: "Choose a process that exists, is published and is not disabled.",
    missing_call_activity_input: "Map every required input of the called process.",
    missing_call_activity_error_boundary: "Attach an error boundary event to this node to follow an error path.",
    call_api_private_host: "The API URL points to a private or loopback address. Use a public endpoint.",
    dispatch_form_without_assignee: "Choose who will receive the dispatched form.",
    approval_queue_without_members: "The approval queue has no members. Add a user to the role.",
    record_source_required: "Choose a record source for this node.",
};

export const STRUCTURED_VALIDATION_CODES = new Set(["invalid_timer_field", "invalid_trigger_condition", "invalid_factory_action_param"]);

interface PendingIssuesPanelProps {
    issues: FlussValidationError[];
    onGoToNode: (elementId: string) => void;
    onOpenCatalog: () => void;
    onOpenTemplate: () => void;
}

export const hasBlockingIssues = (issues: FlussValidationError[]) =>
    issues.some((issue) => issue.severity !== "warning");

const resolveIssueMessage = (
    issue: FlussValidationError,
    t: TFunction,
    i18n: I18n
) => {
    const baseKey = issue.message_key || `fluss.validation.codes.${issue.code}`;
    const reason = issue.params?.reason;
    const structuredKey = reason ? `fluss.validation.reasons.${issue.code}.${reason}` : baseKey;
    if (STRUCTURED_VALIDATION_CODES.has(issue.code) && i18n.exists(structuredKey)) {
        const translatedMessage = t(structuredKey, issue.params || {});
        if (translatedMessage) return translatedMessage;
    }

    if (i18n.language?.toLowerCase().startsWith("pt")) {
        return issue.message || t(baseKey, {
            defaultValue: VALIDATION_MESSAGE_DEFAULTS[issue.code] || "Review this validation issue.",
            ...(issue.params || {}),
        });
    }
    if (i18n.exists(baseKey)) return t(baseKey, issue.params || {});
    return VALIDATION_MESSAGE_DEFAULTS[issue.code]
        || issue.message
        || t(baseKey, { defaultValue: "Review this validation issue.", ...(issue.params || {}) });
};

const PendingIssuesPanel = ({ issues, onGoToNode, onOpenCatalog, onOpenTemplate }: PendingIssuesPanelProps) => {
    const { t, i18n } = useTranslation();
    const blocking = hasBlockingIssues(issues);

    return (
        <Stack gap={1.25} data-testid="pending-issues-panel">
            <Alert severity={blocking ? "error" : "warning"}>
                {blocking
                    ? t("fluss.publish.blocked", { defaultValue: "Resolve the errors before publishing." })
                    : t("fluss.publish.warnings", { defaultValue: "Review the warnings. You may publish after confirmation." })}
            </Alert>
            {issues.map((issue, index) => {
                const warning = issue.severity === "warning";
                const action = issue.action || (issue.element_id ? "node" : null);
                const message = resolveIssueMessage(issue, t, i18n);
                const translatedType = flussNodeTypeLabel(issue.node_type, t);
                const nodeLabel = issue.node_name?.trim() || (translatedType
                    ? `${translatedType} ${t("fluss.publish.unnamed_node", { defaultValue: "(sem nome)" })}`
                    : issue.element_id);
                const nodeTitle = nodeLabel;

                return (
                    <Box
                        key={`${issue.code}-${issue.element_id ?? "global"}-${index}`}
                        data-testid={`validation-issue-${issue.code}`}
                        sx={(theme) => ({
                            border: `1px solid ${warning ? theme.palette.warning.main : theme.palette.error.main}`,
                            borderLeftWidth: 4,
                            borderRadius: 1,
                            p: 1.25,
                        })}
                    >
                        <Stack direction="row" gap={1.25} alignItems="center">
                            {warning ? <WarningAmber color="warning" /> : <ErrorOutline color="error" />}
                            <Box sx={{ flex: 1 }}>
                                {nodeTitle && <Typography variant="subtitle2">{nodeTitle}</Typography>}
                                {issue.element_id && nodeTitle !== issue.element_id && (
                                    <Typography variant="caption" color="text.secondary">{issue.element_id}</Typography>
                                )}
                                <Typography variant="body2">{message}</Typography>
                            </Box>
                            {action === "node" && issue.element_id && (
                                <Button size="small" startIcon={<MyLocation />} onClick={() => onGoToNode(issue.element_id!)}>
                                    {t("fluss.publish.go_to_node", { defaultValue: "Go to node" })}
                                </Button>
                            )}
                            {action === "catalog" && (
                                <Button size="small" startIcon={<OpenInNew />} onClick={onOpenCatalog}>
                                    {t("fluss.publish.open_catalog", { defaultValue: "Open catalog" })}
                                </Button>
                            )}
                            {action === "template" && (
                                <Button size="small" startIcon={<OpenInNew />} onClick={onOpenTemplate}>
                                    {t("fluss.publish.open_template", { defaultValue: "Open template" })}
                                </Button>
                            )}
                        </Stack>
                    </Box>
                );
            })}
        </Stack>
    );
};

export default PendingIssuesPanel;
